/**
 * Editor-level drag & drop state: which block is being dragged and the ONE
 * place it would land (computed centrally — see computeDropTarget). Blocks
 * render an indicator only when they are that single target, which is what
 * guarantees a single indicator line at all times.
 *
 * Reads the live tree through `blocksRef` (same staleness guard as paste /
 * selection) so drops compose with any unflushed typing and always commit
 * through the history-aware path.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { handleBlockDrop, findBlock } from '../utils/blockUtils';
import { flattenBlocksDeep } from '../utils/tableCellBlocks';
import { computeDropTarget } from '../renderer/utils/dropZoneCalculations';

export const useDragAndDrop = (blocksRef, commit, onSelectAfterDrop) => {
  const [activeId, setActiveId] = useState(null);
  const [fileDragActive, setFileDragActive] = useState(false);
  const [dropTarget, setDropTarget] = useState(null); // { targetId, zone } | null
  const dropTargetRef = useRef(null);
  const fileDragActiveRef = useRef(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const applyDropTarget = useCallback((next) => {
    const prev = dropTargetRef.current;
    if (next?.targetId !== prev?.targetId || next?.zone !== prev?.zone) {
      dropTargetRef.current = next;
      setDropTarget(next);
    }
  }, []);

  // Track the cursor during a drag and keep the single drop target current
  useEffect(() => {
    if (!activeId) return undefined;

    // The dragged block and everything inside it can never be a target
    const excludedIds = new Set();
    const dragged = findBlock(blocksRef.current, activeId);
    if (dragged) flattenBlocksDeep([dragged]).forEach((b) => excludedIds.add(b.id));
    else excludedIds.add(activeId);

    const handleMouseMove = (e) => {
      const next = computeDropTarget(
        e.clientX,
        e.clientY,
        blocksRef.current,
        excludedIds,
        activeId
      );
      applyDropTarget(next);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [activeId, applyDropTarget, blocksRef]);

  const resetDragState = useCallback(() => {
    setActiveId(null);
    setDropTarget(null);
    dropTargetRef.current = null;
  }, []);

  const updateFileDropTarget = useCallback(
    (clientX, clientY) => {
      if (!fileDragActiveRef.current) {
        fileDragActiveRef.current = true;
        setFileDragActive(true);
      }
      const next = computeDropTarget(
        clientX,
        clientY,
        blocksRef.current,
        new Set(),
        null
      );
      applyDropTarget(next);
    },
    [applyDropTarget, blocksRef]
  );

  const clearFileDropTarget = useCallback(() => {
    if (!fileDragActiveRef.current && !dropTargetRef.current) return;
    fileDragActiveRef.current = false;
    setFileDragActive(false);
    setDropTarget(null);
    dropTargetRef.current = null;
  }, []);

  const peekDropTarget = useCallback(() => dropTargetRef.current, []);

  const handleDragStart = (event) => {
    // Internal block drag wins over any lingering file-drag indicator
    fileDragActiveRef.current = false;
    setFileDragActive(false);
    dropTargetRef.current = null;
    setDropTarget(null);
    setActiveId(event.active.id);
  };

  // Escape (or any dnd-kit cancellation) aborts cleanly — no stuck state
  const handleDragCancel = () => resetDragState();

  const handleDragEnd = (event) => {
    const finalTarget = dropTargetRef.current;
    const draggedId = event.active.id;
    if (!finalTarget?.targetId || !finalTarget?.zone) {
      resetDragState();
      return;
    }
    // Dropped back on itself — leave the tree alone (no empty undo step)
    if (finalTarget.targetId === draggedId) {
      resetDragState();
      onSelectAfterDrop?.(draggedId);
      return;
    }
    const next = handleBlockDrop(
      blocksRef.current,
      draggedId,
      finalTarget.targetId,
      finalTarget.zone
    );
    // Structural commit (coalesce defaults false) → always its own undo step
    commit(next);
    resetDragState();
    // Keep the moved block selected so Ctrl+Z still fires (focus often lands
    // on <body> after a dnd-kit drag, which would otherwise skip the undo gate)
    onSelectAfterDrop?.(draggedId);
  };

  const activeBlock = activeId ? findBlock(blocksRef.current, activeId) : null;

  return {
    activeId,
    activeBlock,
    dropTarget,
    fileDragActive,
    sensors,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    updateFileDropTarget,
    clearFileDropTarget,
    peekDropTarget,
  };
};
